import { Dialog, Transition } from "@headlessui/react";
import { Fragment, type ReactNode, useRef } from "react";
import { useForm } from "react-hook-form";
import clsx from "clsx";
import { FaTimes } from "react-icons/fa";

export interface ISendToFinanceProps {
  reason: string;
}

interface IFinanceRejectRequestModalProps {
  open: boolean;
  onSave?: (reason: string) => void;
  onCustomSubmit?: () => void | Promise<void>;
  customContent?: ReactNode;
  isSubmitDisabled?: boolean;
  panelClassName?: string;
  setOpen: (value: boolean) => void;
  title?: string;
  submitLabel?: string;
  placeholder?: string;
}

export default function FinanceRejectRequestModal({
  open,
  onSave,
  onCustomSubmit,
  customContent,
  isSubmitDisabled = false,
  panelClassName,
  setOpen,
  title = "Are you sure on Rejecting Invoice ?",
  submitLabel = "Escalate it",
  placeholder = "Please enter reason",
}: IFinanceRejectRequestModalProps) {
  const cancelButtonRef = useRef(null);

  const formHook = useForm<ISendToFinanceProps>({
    mode: "onChange",
  });
  const isReasonFilled = !!formHook.watch("reason")?.trim().length;

  const handleClose = () => {
    setOpen(false);
    formHook.reset({ reason: "" });
  };

  return (
    <div>
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-40" initialFocus={cancelButtonRef} onClose={handleClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel
                  className={clsx(
                    "relative flex transform flex-col rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-4 sm:w-[480px] sm:p-6",
                    panelClassName,
                  )}
                >
                  <button onClick={handleClose} className="absolute top-6 right-3 text-gray-500 hover:text-gray-700">
                    <FaTimes size={16} />
                  </button>
                  <div className="pb-2">
                    <div className="font-semibold text-sm">{title}</div>
                  </div>
                  {customContent ? (
                    <div className="mt-3">
                      {customContent}
                      <div className="mt-4 flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={handleClose}
                          className="rounded-md border border-gray-300 px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => onCustomSubmit?.()}
                          disabled={isSubmitDisabled}
                          className={clsx(
                            "rounded-md bg-black px-4 py-2 text-xs font-medium text-white",
                            isSubmitDisabled && "cursor-not-allowed opacity-50",
                          )}
                        >
                          {submitLabel}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="mt-5">
                        <div className="w-full">
                          <textarea
                            rows={4}
                            style={{
                              overflowY: "scroll",
                              scrollbarWidth: "thin",
                            }}
                            className={clsx(
                              `rounded-md w-full h-full border text-xs`,
                              formHook?.formState?.errors?.reason ? "outline-red-300 border-red-600" : "border-gray-300",
                            )}
                            placeholder={placeholder}
                            {...formHook.register(`reason`, {
                              required: true,
                              minLength: 1,
                            })}
                          />
                          {formHook?.formState?.errors?.reason && (
                            <p className="text-red-500 absolute text-[10px] -mt-1">Reason is required</p>
                          )}
                        </div>
                      </div>
                      <div className="flex justify-end mt-3">
                        <button
                          onClick={formHook.handleSubmit((data) => {
                            onSave?.(data.reason);
                            formHook.reset({ reason: "" });
                          })}
                          disabled={!isReasonFilled}
                          className={clsx(
                            "px-4 py-2 rounded-md text-white bg-black flex justify-center w-full ",
                            !isReasonFilled && "opacity-50 cursor-not-allowed",
                          )}
                        >
                          {submitLabel}
                        </button>
                      </div>
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
}
